/**
 * 요청 상세 — /catalog-entry/requests/:pendingId (VOCABULARY "요청 상세" · 2026-10-14 저녁).
 * 요청함 줄을 눌러 새 탭으로 여는 한 건. 머리 카드(PN · 상태 · 요청 글 · 출처 · 왜) · 메모 카드(사람의 운영 메모 · 따로 저장) · 초안 카드(파트넘버 · 표 · 칸 표 전면) · 버튼 바(초안 저장 · 승격 · 보류 · 반려).
 * 승격은 서버가 한 트랜잭션으로(카탈로그 행 + product 채움) — 화면은 칸을 모아 보낼 뿐.
 */
import { SolaView, htmlSolaWithInstance } from '@hm-spt/sola';
import { toSpecCategory } from '@hcltech/shared/server-config/spec-category';
import * as api from '../api/client';
import type { CatalogRequestView, DescriptionReading, DraftMatchView, ECatalogTable, ModelRowView, TableColumnsView } from '../api/client';
import { assert } from '../lib/assert';
import { openMergeDialog } from '../components/merge-dialog';
import { focusedSlotOrNull, refocusSlot, slotRoleOf, trimmedFillsOf, withSlotFill } from '../components/slot-reading';
import { draftOf, formOf, isNumberColumn, modelEvidenceKeyOf, numbersOf, rawOf, tableOrNull } from './catalog-requests/form';
import { renderActionsOf, renderDescriptionReading, renderRequestDetail } from './catalog-requests/render';
import type { CatalogRequestDetailData, DraftForm } from './catalog-requests/types';

/** [지금 조사] · [다시 조사] 끝난 뒤 안내 — 서버가 루틴을 바로 돌리니 몇 분이면 온다(일정 :35 는 보험) */
const MODEL_LOOKUP_DEBOUNCE_MS = 400;
const MATCH_DEBOUNCE_MS = 300;   // 번호 칸이 바뀌고 카탈로그를 다시 찾기까지(2026-10-20)
const READING_DEBOUNCE_MS = 300; // 원문 · 채운 자리를 치는 동안 글자마다 규칙 읽기를 안 부른다(2026-10-26)
const RAW_COLUMN = 'description_raw';
const RESEARCH_DONE_WORD = 'LLM 이 이 건부터 바로 조사합니다. 몇 분 뒤 확인 대기에서 보세요.';
const RESEARCH_NEEDS_TABLE_WORD = '카테고리를 골라 주세요 — 조사할 칸은 카테고리가 정합니다.';
const HOLD_NEEDS_NOTES_WORD = '보류엔 메모가 필요합니다 — 메모 칸에 무엇이 걸렸는지 먼저 적어 주세요.';
const CLOSE_ASK = '이 요청을 종료합니다 — 카탈로그 행은 그대로 두고, 요청을 낸 제품만 그 행에 잇습니다. 계속할까요?';   // [요청 종료](2026-10-20 밤)   // 표 없던 요청(옛 길)에서 [지금 조사] 를 눌렀을 때

export class CatalogRequestDetailView extends SolaView<CatalogRequestDetailData> {
  override events = {
    click:  { '@save': 'handleSave', '@promote': 'handlePromote', '@reject': 'handleReject', '@research': 'handleResearch', '@hold': 'handleHold', '@release': 'handleRelease', '@save-notes': 'handleSaveNotes',
              '@close-in-catalog': 'handleCloseInCatalog' },   // [요청 종료] — 옵션 PN 이 카탈로그에 있을 때(2026-10-20 밤)
    input:  { '@slot-fill': 'handleSlotFill', '@notes': 'handleNotes', '@pn': 'handlePn', '@cell': 'handleCell', '@evidence': 'handleEvidence', '@mcell': 'handleModelCell', '@mevidence': 'handleModelEvidence' },
    change: { '@table': 'handleTable', '@cell': 'handleCell', '@mcell': 'handleModelCell' },   // 라디오(예/아니오)는 change 로 온다
  };

  private modelLookupTimer: ReturnType<typeof setTimeout> | null = null;
  private matchTimer: ReturnType<typeof setTimeout> | null = null;
  private readingTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(params: { pendingId: number }) {
    super();
    this.data = { pendingId: params.pendingId, view: null, columns: [], model: null, modelRow: null, form: { option_part: '', table: null, values: {}, evidence: {}, model_values: {}, description_fills: [] }, reading: null, match: null, notesDraft: '', loading: true, saving: false, error: '' };
  }

  override getTitle(): string {
    return `카탈로그 요청 #${this.data.pendingId} — HCLTech`;
  }

  override onMount(): void {
    void this.load();
  }

  render(): HTMLElement {
    return htmlSolaWithInstance(this)`
      <div class="page page-catalog-request-detail pv-page">
        <header class="page-header">
          <h1>📋 카탈로그 요청</h1>
          <a class="btn-ghost" data-link href="/catalog-entry/requests">요청함으로</a>
        </header>
        <section class="page-body pv-body cr-detail-body">${renderRequestDetail(this.data)}</section>
      </div>`;
  }

  // ─── 입력 ───
  handlePn(_e: Event, el: HTMLElement): void {
    this.data.form.option_part = (el as HTMLInputElement).value;
    this.scheduleMatch();
  }

  handleCell(e: Event, el: HTMLElement): void {
    const key = el.dataset.key;
    assert(key !== undefined, '칸 입력은 data-key(열 이름)를 가진다 — renderDraftCells 가 붙였다');
    this.data.form.values = { ...this.data.form.values, [key]: (el as HTMLInputElement).value };
    if (this.data.model !== null && key === this.data.model.key_column) this.scheduleModelLookup();
    if (isNumberColumn(key)) this.scheduleMatch();
    if (key === RAW_COLUMN) this.scheduleReading();
    if (isNumberInputLeft(e, el)) this.rerender();   // 숫자 칸을 떠나면 붉은 줄 · 버튼 바를 다시 본다(치는 동안은 안 그린다 — 포커스, 2026-10-27)
  }

  /** 약식 상자의 자리 칸 — data-key = 역할(assert) → withSlotFill → scheduleReading(상자를 바로 안 다시 그려 포커스) */
  handleSlotFill(_e: Event, el: HTMLElement): void {
    const role = slotRoleOf(el.dataset.key);
    this.data.form.description_fills = withSlotFill(this.data.form.description_fills, role, (el as HTMLInputElement).value);
    this.scheduleReading();
  }

  /** 모델 칸 입력 — model_values 의 그 키 */
  handleModelCell(e: Event, el: HTMLElement): void {
    const key = el.dataset.key;
    assert(key !== undefined, '모델 칸 입력은 data-key(모델 표 열 이름)를 가진다 — renderModelCells 가 붙였다');
    this.data.form.model_values = { ...this.data.form.model_values, [key]: (el as HTMLInputElement).value };
    if (isNumberInputLeft(e, el)) this.rerender();   // 부품 칸과 같다 — 모델 칸의 자리 넘침도 떠날 때 붉게
  }

  /** 모델 칸 근거 — evidence 의 'model.<열>' */
  handleModelEvidence(_e: Event, el: HTMLElement): void {
    const key = el.dataset.key;
    assert(key !== undefined, '모델 근거 입력은 data-key(모델 표 열 이름)를 가진다 — renderModelCells 가 붙였다');
    this.data.form.evidence = { ...this.data.form.evidence, [modelEvidenceKeyOf(key)]: (el as HTMLInputElement).value };
  }

  handleEvidence(_e: Event, el: HTMLElement): void {
    const key = el.dataset.key;
    assert(key !== undefined, '근거 입력은 data-key(열 이름)를 가진다 — renderDraftCells 가 붙였다');
    this.data.form.evidence = { ...this.data.form.evidence, [key]: (el as HTMLInputElement).value };
  }

  /** 표를 바꾸면 그 표의 칸을 받아 다시 그린다. 값은 남긴다(같은 이름의 열은 그대로 보인다) */
  async handleTable(_e: Event, el: HTMLElement): Promise<void> {
    await this.loadTable(tableOrNull((el as HTMLSelectElement).value));
  }

  /** 표의 칸(+ 모델 칸)을 받아 폼을 다시 그린다. 모델 열쇠가 있으면 모델 행도 찾는다 */
  private async loadTable(table: ECatalogTable | null): Promise<void> {
    this.data.form.table = table;
    this.data.form.description_fills = [];   // 새 규칙에 없는 역할이 꼬리로 붙지 않게(제품 폼과 같은 규칙)
    this.data.reading = null;
    this.data.columns = [];
    this.data.model = null;
    this.data.modelRow = null;
    this.rerender();
    if (table === null) return;
    const columns: TableColumnsView = await api.fetchTableColumns(table);
    if (this.data.form.table !== table) return;   // 기다리는 사이 또 바뀌었으면 버린다
    this.data.columns = columns.rows;
    this.data.model = columns.model;
    this.rerender();
    await this.refreshReading();
    await this.lookupModelRow();
  }

  /** 열쇠 칸이 바뀌면 400ms 뒤 모델 표를 찾아본다(치는 동안은 안 묻는다) */
  private scheduleModelLookup(): void {
    if (this.modelLookupTimer !== null) clearTimeout(this.modelLookupTimer);
    this.modelLookupTimer = setTimeout(() => { this.modelLookupTimer = null; void this.lookupModelRow(); }, MODEL_LOOKUP_DEBOUNCE_MS);
  }

  // ─── 약식 읽기(2026-10-26 승격도 규칙이) ───
  /** 원문 · 채운 자리를 치는 동안 — READING_DEBOUNCE_MS 뒤 refreshReading */
  private scheduleReading(): void {
    if (this.readingTimer !== null) clearTimeout(this.readingTimer);
    this.readingTimer = setTimeout(() => { this.readingTimer = null; void this.refreshReading(); }, READING_DEBOUNCE_MS);
  }

  /**
   * 원문을 그 표 카테고리의 기본 규칙으로 읽는다 — 서버가 승격 때 만드는 약식과 같다. 표 · 원문이 없으면 reading null.
   * 받는 동안 앞 결과를 그대로 둔다(깜박임). 열쇠(표 · 원문 · 채운 자리)가 그사이 바뀌었으면 버린다. 상자와 버튼 바만 다시 그린다(포커스)
   */
  private async refreshReading(): Promise<void> {
    const snapshot: ReadingKey = readingKeyOf(this.data.form);   // 폼 객체는 칸마다 고쳐 쓰니 값으로 떠 둔다
    const { table, raw } = snapshot;
    const category = table === null ? null : toSpecCategory(table);
    if (category === null || raw === '') {
      this.data.reading = null;
      this.refreshReadingRegion();
      return;
    }
    let reading: DescriptionReading;
    try {
      reading = await api.readDescription({ spec_category: category, raw, rule_id: null, fills: trimmedFillsOf(snapshot.fills) });
    } catch (e) {
      this.data.error = e instanceof Error ? e.message : String(e);
      this.rerender();
      return;
    }
    if (!isSameReadingKey(snapshot, readingKeyOf(this.data.form))) return;
    this.data.reading = reading;
    this.refreshReadingRegion();
  }

  /** 상자와 버튼 바만 다시 그린다 — 포커스된 자리 칸(역할 · 커서)을 되돌린다. 버튼 바엔 글 칸이 없어 포커스가 안 끊긴다 */
  private refreshReadingRegion(): void {
    const focused = focusedSlotOrNull();
    this.refreshRegion('.cr-reading', () => renderDescriptionReading(this.data));
    this.refreshRegion('.cr-actions-slot', () => renderActionsOf(this.data));
    if (focused !== null) refocusSlot(this._rootElement ?? null, '.cr-reading', focused);
  }

  // ─── 번호 찾기(2026-10-20) ───
  /** 번호 칸이 바뀌면 300ms 뒤 카탈로그를 찾는다(치는 동안은 안 묻는다) */
  private scheduleMatch(): void {
    if (this.matchTimer !== null) clearTimeout(this.matchTimer);
    this.matchTimer = setTimeout(() => { this.matchTimer = null; void this.loadMatch(); }, MATCH_DEBOUNCE_MS);
  }

  /** 번호 넷으로 맞은 행 · 판정 — 옵션 PN 이 카탈로그에 있으면 화면이 [승격] 대신 [요청 종료] 를 보인다 */
  private async loadMatch(): Promise<void> {
    const match: DraftMatchView = await api.fetchDraftMatches(numbersOf(this.data.form));
    this.data.match = match;
    this.rerender();
  }

  /** 열쇠 값으로 모델 행을 받아 modelRow 에. 열쇠가 비었거나 모델 FK 표가 아니면 null. 비교 중이면 모델 블록(열쇠 같은가)도 다시 받는다 */
  private async lookupModelRow(): Promise<void> {
    const model = this.data.model;
    const table = this.data.form.table;
    const key = model === null ? '' : (this.data.form.values[model.key_column] ?? '').trim();
    if (model === null || table === null || key === '') {
      this.data.modelRow = null;
      this.rerender();
      return;
    }
    try {
      const row: ModelRowView = await api.fetchModelRow(table, key);
      if (this.data.form.table === table) this.data.modelRow = row;
    } catch (e) {
      this.data.error = e instanceof Error ? e.message : String(e);
    }
    this.rerender();
  }

  // ─── 버튼 셋 ───
  async handleSave(): Promise<void> {
    await this.submit(async () => {
      const view = await api.saveCatalogDraft(this.data.pendingId, draftOf(this.data.form, this.data.columns, this.data.model));
      this.takeView(view);
    });
  }

  async handlePromote(): Promise<void> {
    await this.submit(async () => {
      try {
        await this.promote();
      } catch (e) {
        if (!(e instanceof api.ProductConflictError)) throw e;
        const bMerged = await this.mergeIntoConflict(e);
        if (!bMerged) throw e;   // 취소 — 409 글을 그대로 띄운다
        await this.promote();    // 합친 뒤 다시 승격
      }
    });
  }

  /** 승격 한 번 — 새 행. 끝나면 그 줄을 다시 받고 결과를 알린다 */
  private async promote(): Promise<void> {
    const result = await api.promoteCatalogPart(this.data.pendingId, draftOf(this.data.form, this.data.columns, this.data.model));
    this.takeView(await api.fetchCatalogRequest(this.data.pendingId));
    alert(`승격 — ${result.table} #${result.row_id} 새 행${result.products_filled === 0 ? '' : ` · 제품 ${result.products_filled}건 연결`}`);
  }

  /** [요청 종료] — 옵션 PN 이 카탈로그에 이미 있을 때. 이 요청만 닫고 제품을 그 행에 잇는다(카탈로그는 그대로) */
  async handleCloseInCatalog(): Promise<void> {
    if (!confirm(CLOSE_ASK)) return;
    await this.submit(async () => {
      try {
        this.takeView(await api.closeRequestInCatalog(this.data.pendingId));
      } catch (e) {
        if (!(e instanceof api.ProductConflictError)) throw e;
        const bMerged = await this.mergeIntoConflict(e);
        if (!bMerged) throw e;   // 취소 — 409 글을 그대로 띄운다
        this.takeView(await api.closeRequestInCatalog(this.data.pendingId));   // 합친 뒤 다시 종료
      }
    });
  }

  /** 승격 · 요청 종료 409 의 짝으로 막힌 제품(없어짐)을 합친다. 짝은 같은 SKU 서버 제품이거나 그 카탈로그 행 · PN 에 이미 이어진 제품(2026-10-30). 막힌 제품은 화면의 제품이 아닐 수 있어 409 가 싣는 것을 쓴다(2026-10-31). 합쳤으면 true */
  private async mergeIntoConflict(conflict: api.ProductConflictError): Promise<boolean> {
    const dropId = conflict.blockedProductIdOrNull;
    assert(dropId !== null, '요청 쪽 409(승격 · 요청 종료)는 막힌 제품 blocked_product_id 를 늘 싣는다 — 백엔드 fillRequestProduct 의 RequestProductConflict. 없으면 PN 정정 409 가 여기로 잘못 왔다');
    const detail = await openMergeDialog(conflict.product.id, dropId, 'promote');
    return detail !== null;
  }

  /** [지금 조사] · [다시 조사] — 묻지 않고 바로. 빈 칸은 LLM 이 지난 초안을 보고 스스로 고른다(현민 2026-10-14 밤 "null 인 건 다 확인하는 거라니까") */
  async handleResearch(): Promise<void> {
    if (this.data.form.table === null) {
      alert(RESEARCH_NEEDS_TABLE_WORD);
      return;
    }
    await this.submit(async () => {
      this.takeView(await api.startCatalogResearch(this.data.pendingId, this.data.form.table));
      alert(RESEARCH_DONE_WORD);
    });
  }

  async handleReject(): Promise<void> {
    const reason = prompt('반려 이유');
    if (reason === null || reason.trim() === '') return;
    await this.submit(async () => {
      this.takeView(await api.rejectCatalogPart(this.data.pendingId, reason.trim()));
    });
  }

  // ─── 메모 · 보류 — 폼(초안 칸)은 그대로 둔다. 저장 안 한 칸을 날리지 않는다 ───
  handleNotes(_e: Event, el: HTMLElement): void {
    this.data.notesDraft = (el as HTMLTextAreaElement).value;
  }

  async handleSaveNotes(): Promise<void> {
    await this.submit(async () => {
      this.takeStatus(await api.updateCatalogRequestNotes(this.data.pendingId, this.data.notesDraft));
    });
  }

  /** [보류] — 메모 칸의 글을 메모로 함께 보낸다. 비었으면 묻지 않고 막는다(회의 2026-10-15 "의견이 들어간 상태") */
  async handleHold(): Promise<void> {
    if (this.data.notesDraft.trim() === '') {
      alert(HOLD_NEEDS_NOTES_WORD);
      return;
    }
    await this.submit(async () => {
      this.takeStatus(await api.holdCatalogPart(this.data.pendingId, this.data.notesDraft.trim()));
    });
  }

  async handleRelease(): Promise<void> {
    await this.submit(async () => {
      this.takeStatus(await api.releaseCatalogPart(this.data.pendingId));
    });
  }

  // ─── 안쪽 ───
  /** 한 건 + 표가 있으면 칸. 404 는 "없는 요청" */
  private async load(): Promise<void> {
    this.data = { ...this.data, loading: true, error: '' };
    this.rerender();
    try {
      const view = await api.fetchCatalogRequest(this.data.pendingId);
      const form = formOf(view);
      const columns: TableColumnsView = form.table === null ? { rows: [], model: null } : await api.fetchTableColumns(form.table);
      this.data = { ...this.data, view, form, notesDraft: view.notes ?? '', columns: columns.rows, model: columns.model, modelRow: null, match: null, loading: false };
    } catch (e) {
      this.data = { ...this.data, view: null, loading: false, error: e instanceof Error ? e.message : String(e) };
    }
    this.rerender();
    await this.refreshReading();
    await this.lookupModelRow();
    if (this.data.view !== null && this.data.view.status !== 'registered' && this.data.view.status !== 'rejected') await this.loadMatch();   // 열린 요청만 — 번호가 이미 있는 행인가
  }

  /** 서버가 돌려준 그 건으로 — 폼도 그 초안으로 다시. 찾은 결과는 놓는다(끝난 요청이면 안 보인다) */
  private takeView(view: CatalogRequestView): void {
    this.data = { ...this.data, view, form: formOf(view), notesDraft: view.notes ?? '', match: null };
    void this.refreshReading();   // 폼이 그 초안으로 바뀌었다 — 채운 자리는 비고 원문은 저장된 것
  }

  /** 메모 · 보류 · 풀기 뒤 — 상태와 메모만 받는다. 초안 칸(form)은 사람이 치던 그대로 */
  private takeStatus(view: CatalogRequestView): void {
    this.data = { ...this.data, view, notesDraft: view.notes ?? '' };
  }

  /** 저장 · 승격 · 반려 공통 — 잠그고 돌리고. 실패는 페이지 안 오류 글 */
  private async submit(work: () => Promise<void>): Promise<void> {
    this.data = { ...this.data, saving: true, error: '' };
    this.rerender();
    try {
      await work();
    } catch (e) {
      this.data.error = e instanceof Error ? e.message : String(e);
    }
    this.data.saving = false;
    this.rerender();
  }

  private rerender(): void {
    this.refreshRegion('.cr-detail-body', () => renderRequestDetail(this.data));
  }
}

/** 약식 읽기의 열쇠 셋 — 표 · 원문 · 채운 자리 */
type ReadingKey = { table: ECatalogTable | null; raw: string; fills: DraftForm['description_fills'] };

/** 숫자 입력을 떠났나(change) — 치는 중(input)엔 다시 그리지 않는다. 숫자 칸은 renderCellInput 이 type="number" 로 그린다 */
function isNumberInputLeft(e: Event, el: HTMLElement): boolean {
  return e.type === 'change' && el instanceof HTMLInputElement && el.type === 'number';
}

function readingKeyOf(form: DraftForm): ReadingKey {
  return { table: form.table, raw: rawOf(form), fills: form.description_fills };
}

/** 답을 받는 사이 사람이 바꿨으면 그 답은 버린다. 채운 자리는 고칠 때마다 새 배열 — 참조로 견준다 */
function isSameReadingKey(a: ReadingKey, b: ReadingKey): boolean {
  return a.table === b.table && a.raw === b.raw && a.fills === b.fills;
}
